import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Loader2, Download, ArrowLeft, RefreshCw } from "lucide-react";
import UpdatesManager from "./UpdatesManager";
import TestimonialsManager from "./TestimonialsManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLS = ["created_at", "full_name", "email", "phone", "location", "journey", "consent", "guide_email_sent"];

const csvCell = (v) => {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export default function LeadsAdmin() {
  const [secret, setSecret] = useState(sessionStorage.getItem("fba_admin_secret") || "");
  const [leads, setLeads] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("leads");

  const load = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/leads`, { headers: { "X-Admin-Secret": secret } });
      sessionStorage.setItem("fba_admin_secret", secret);
      setLeads(res.data);
    } catch (err) {
      if (err.response?.status === 401) setError("Invalid admin secret. Please check and try again.");
      else setError("Could not load leads. Please try again.");
      setLeads(null);
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (!leads?.length) return;
    const header = COLS.join(",");
    const rows = leads.map((l) => COLS.map((c) => csvCell(l[c])).join(","));
    const blob = new Blob([[header, ...rows].join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fiji-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="grain no-x-overflow relative min-h-screen bg-[var(--page)] text-[var(--ink)]" data-testid="leads-admin-page">
      <div className="border-b border-[var(--hairline)]">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="admin-home-link">
            <span className="h-8 w-8 rounded-[3px] bg-[var(--ink)] flex items-center justify-center">
              <span className="font-display text-[var(--page)] text-lg leading-none">F</span>
            </span>
            <span className="font-display text-base sm:text-xl leading-none tracking-tight">Leads Dashboard</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[var(--muted-ink)] hover:text-[var(--ink)]">
            <ArrowLeft className="h-4 w-4" /> Site
          </Link>
        </div>
      </div>

      <main className="mx-auto max-w-[1400px] px-5 sm:px-8 py-12">
        {!leads ? (
          <form onSubmit={load} className="max-w-md" data-testid="admin-login-form">
            <h1 className="font-display font-semibold tracking-tight text-3xl sm:text-4xl">Team access</h1>
            <p className="mt-3 text-[var(--muted-ink)]">Enter the admin secret to view captured leads.</p>
            <input
              type="password"
              data-testid="admin-secret-input"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder="Admin secret"
              className="mt-6 w-full bg-transparent border-b border-[var(--hairline)] py-3 text-lg focus:outline-none focus:border-[var(--ink)] transition-colors"
            />
            {error && <p className="mt-3 text-sm text-[var(--ink)]" data-testid="admin-error">{error}</p>}
            <button
              type="submit"
              data-testid="admin-login-btn"
              disabled={loading || !secret}
              className="mt-6 inline-flex items-center gap-2 px-8 py-4 rounded-[3px] bg-[var(--ink)] text-[var(--page)] text-sm font-semibold uppercase tracking-wide transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Loading…</> : "View leads"}
            </button>
          </form>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-8 border-b border-[var(--hairline)]">
              <button
                onClick={() => setTab("leads")}
                data-testid="tab-leads"
                className={`px-1 pb-3 -mb-px text-sm font-semibold uppercase tracking-wide border-b-2 transition-colors ${tab === "leads" ? "border-[var(--ink)] text-[var(--ink)]" : "border-transparent text-[var(--muted-ink)] hover:text-[var(--ink)]"}`}
              >
                Leads
              </button>
              <button
                onClick={() => setTab("updates")}
                data-testid="tab-updates"
                className={`ml-6 px-1 pb-3 -mb-px text-sm font-semibold uppercase tracking-wide border-b-2 transition-colors ${tab === "updates" ? "border-[var(--ink)] text-[var(--ink)]" : "border-transparent text-[var(--muted-ink)] hover:text-[var(--ink)]"}`}
              >
                Updates
              </button>
              <button
                onClick={() => setTab("testimonials")}
                data-testid="tab-testimonials"
                className={`ml-6 px-1 pb-3 -mb-px text-sm font-semibold uppercase tracking-wide border-b-2 transition-colors ${tab === "testimonials" ? "border-[var(--ink)] text-[var(--ink)]" : "border-transparent text-[var(--muted-ink)] hover:text-[var(--ink)]"}`}
              >
                Testimonials
              </button>
            </div>

            {tab === "testimonials" ? (
              <TestimonialsManager secret={secret} />
            ) : tab === "updates" ? (
              <UpdatesManager secret={secret} />
            ) : (
            <>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h1 className="font-display font-semibold tracking-tight text-3xl sm:text-4xl">Captured leads</h1>
                <p className="mt-2 text-[var(--muted-ink)]" data-testid="leads-count">{leads.length} total</p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={load} data-testid="admin-refresh-btn" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[3px] border border-[var(--hairline)] text-xs font-semibold uppercase tracking-wide hover:bg-[var(--ink)]/5">
                  <RefreshCw className="h-4 w-4" /> Refresh
                </button>
                <button onClick={exportCsv} data-testid="admin-export-csv" disabled={!leads.length} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[3px] bg-[var(--ink)] text-[var(--page)] text-xs font-semibold uppercase tracking-wide hover:opacity-90 disabled:opacity-50">
                  <Download className="h-4 w-4" /> Export CSV
                </button>
              </div>
            </div>

            <div className="mt-8 overflow-x-auto border border-[var(--hairline)] rounded-[4px]">
              <table className="w-full text-left text-sm" data-testid="leads-table">
                <thead>
                  <tr className="border-b border-[var(--hairline)] bg-[var(--ink)]/[0.03]">
                    <th className="px-4 py-3 font-semibold uppercase tracking-wide text-xs whitespace-nowrap">Received</th>
                    <th className="px-4 py-3 font-semibold uppercase tracking-wide text-xs">Name</th>
                    <th className="px-4 py-3 font-semibold uppercase tracking-wide text-xs">Email</th>
                    <th className="px-4 py-3 font-semibold uppercase tracking-wide text-xs">Phone</th>
                    <th className="px-4 py-3 font-semibold uppercase tracking-wide text-xs">Location</th>
                    <th className="px-4 py-3 font-semibold uppercase tracking-wide text-xs">Journey</th>
                    <th className="px-4 py-3 font-semibold uppercase tracking-wide text-xs whitespace-nowrap">Email sent</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-10 text-center text-[var(--muted-ink)]">No leads yet.</td></tr>
                  ) : leads.map((l) => (
                    <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-[var(--hairline)] last:border-0 hover:bg-[var(--ink)]/[0.02]">
                      <td className="px-4 py-3 whitespace-nowrap text-[var(--muted-ink)]">{fmt(l.created_at)}</td>
                      <td className="px-4 py-3">{l.full_name}</td>
                      <td className="px-4 py-3"><a href={`mailto:${l.email}`} className="underline underline-offset-2">{l.email}</a></td>
                      <td className="px-4 py-3 whitespace-nowrap">{l.phone}</td>
                      <td className="px-4 py-3">{l.location || "—"}</td>
                      <td className="px-4 py-3 capitalize">{l.journey}</td>
                      <td className="px-4 py-3">{l.guide_email_sent ? "Yes" : "No"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
